/**
 * 任務角標——把「今日還剩幾項核心任務」印到主畫面的 App 圖示上。
 *
 * 這是**完成度的另一種呈現**，不是新的計算（見 CONTEXT.md）。因此有一條
 * 不變式：角標的數字必須永遠等於首頁那行「n / 5 完成」的剩餘數。呼叫端
 * 的做法是在每次重繪之後同步一次，讓兩者不可能分岔。
 *
 * 唯一分岔得了的時機是 App 關著的時候：PWA 沒有背景執行，跨過午夜之後
 * 角標會停在昨天的數字，直到你再打開 App。要真正每天自動重設得靠 Web Push
 * （VAPID ＋ 訂閱表 ＋ 獨立 Worker cron），刻意沒做。
 *
 * 副作用都在這裡（navigator 與 localStorage），所以放 storage/ 而不是 domain/。
 */

const BADGE_KEY = "fight-health-badge-v1";

export interface BadgeStatus {
  /** 瀏覽器有沒有 Badging API。沒有的話設定頁那張卡整張不顯示。 */
  supported: boolean;
  /** 使用者自己的偏好，預設關。 */
  enabled: boolean;
  /**
   * 通知權限被拒。iOS 的主畫面 web app 要有通知權限才畫得出角標，
   * 桌機則不需要——所以這是提示而不是失敗，開關仍然可以是開的。
   */
  blocked: boolean;
}

function supported(): boolean {
  return typeof navigator !== "undefined" && "setAppBadge" in navigator;
}

function blocked(): boolean {
  return typeof Notification !== "undefined" && Notification.permission === "denied";
}

function readPreference(): boolean {
  try {
    return localStorage.getItem(BADGE_KEY) === "on";
  } catch {
    return false;
  }
}

export function badgeStatus(): BadgeStatus {
  return { supported: supported(), enabled: supported() && readPreference(), blocked: blocked() };
}

/**
 * 開啟時順便要一次通知權限——iOS 沒有它就不會畫角標。這個呼叫必須發生在
 * 使用者手勢的呼叫堆疊裡，所以只能由設定頁那個開關觸發，不能在載入時自動跑。
 *
 * 權限被拒不算開啟失敗：桌機不需要通知權限也能顯示角標。
 */
export async function setBadgeEnabled(on: boolean): Promise<BadgeStatus> {
  if (!supported()) return badgeStatus();

  if (on && typeof Notification !== "undefined" && Notification.permission === "default") {
    try { await Notification.requestPermission(); } catch { /* 拒絕或不支援都當作沒權限 */ }
  }

  try { localStorage.setItem(BADGE_KEY, on ? "on" : "off"); } catch { /* 無痕模式寫不進去就算了 */ }
  if (!on) void navigator.clearAppBadge?.();

  return badgeStatus();
}

/**
 * 把剩餘項數同步到圖示。關閉或不支援時什麼都不做。
 *
 * 剩 0 項就清掉角標——`setAppBadge(0)` 依規格等同清除，這裡明寫成
 * clearAppBadge 只是讓意圖直接可讀。
 */
export function syncAppBadge(remaining: number): void {
  if (!supported() || !readPreference()) return;
  const count = Math.max(0, Math.round(remaining));
  const done = count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
  // 失敗（例如 iOS 沒給通知權限）不該讓呼叫端的重繪爆掉
  void done?.catch?.(() => {});
}
